import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PublicLayout, { Eyebrow, PrimaryBtn, GhostBtn, CheckItem, H2 } from "@/components/public/PublicLayout";
import { ROUTES, C, SERIF } from "@/lib/publicLinks";
import { AssessmentTiers } from "@/components/public/AssessmentTiers";
import { DEFAULT_HOME } from "@/lib/homeContent";

const SIGNS = [
  "Changes in energy", "Poor sleep", "Increased stress", "Digestive concerns",
  "Weight changes", "Nutrition concerns", "Hair or skin changes",
  "A sense that something feels different, even when you're not sure why",
];
const LOOK = ["Nutrition", "Sleep", "Stress", "Digestion", "Energy", "Weight", "Hair & Skin", "Lifestyle Patterns"];
const STEPS = [
  { n: "01", t: "Assess", d: "Complete a virtual functional wellness assessment." },
  { n: "02", t: "Understand", d: "Receive individualized wellness findings identifying relevant patterns and areas that may deserve attention." },
  { n: "03", t: "Act", d: "Receive appropriate wellness recommendations and determine whether additional support, an in-clinic service, or a structured wellness program may be beneficial." },
];
const TIER_WHO = DEFAULT_HOME.tiers.who;

export default function FunctionalWellness() {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return undefined;
    const t = setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    return () => clearTimeout(t);
  }, [hash]);
  return (
    <PublicLayout>
      <section style={{ background: C.lightBg }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <Eyebrow>Functional Wellness</Eyebrow>
          <h1 className="text-4xl sm:text-5xl leading-[1.05] text-slate-900 max-w-3xl" style={SERIF} data-testid="wellness-h1">You Don't Have to Keep Guessing.</h1>
          <p className="mt-6 text-lg text-slate-600 max-w-2xl">When several things seem to change at once — energy, sleep, stress, digestion, weight, hair, or how you feel overall — looking at one symptom at a time may not tell the whole story. KISSED helps you step back and look at the patterns together.</p>
          <div className="mt-8">
            <a href="#choose-depth" className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white transition shadow-sm hover:opacity-90" style={{ background: C.primary }} data-testid="wellness-hero-cta">Choose My Assessment <ArrowRight className="h-4 w-4" /></a>
          </div>
        </div>
      </section>

      {/* Tiers */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" id="choose-depth" style={{ scrollMarginTop: 80 }}>
        <Eyebrow>Choose How Deep You Want to Go</Eyebrow>
        <H2>KISSED Wellness Assessment™</H2>
        <p className="mt-3 text-slate-600 max-w-2xl">Three levels of depth, completed online from home. Each level includes everything in the level before it.</p>
        <div className="mt-10"><AssessmentTiers who={TIER_WHO} /></div>
        <p className="mt-6 text-sm font-medium" style={{ color: C.primary }} data-testid="wellness-tiers-note">Each new assessment is a separate purchase. Previously purchased assessments remain listed in your Client Care Hub.</p>
        <p className="mt-6 text-sm text-slate-500">After payment, you'll be redirected to begin your Clinical Deep Dive™. Prefer to begin inside the clinic system first? <Link to={ROUTES.wellnessAssessment} className="font-medium hover:underline" style={{ color: C.accent }}>Start a wellness assessment</Link>.</p>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Eyebrow>Does This Sound Like You?</Eyebrow>
        <H2 className="max-w-2xl">Your Body May Be Trying to Tell You Something.</H2>
        <ul className="mt-8 grid sm:grid-cols-2 gap-x-10 gap-y-4 max-w-3xl">{SIGNS.map((s) => <CheckItem key={s}>{s}</CheckItem>)}</ul>
      </section>

      <section style={{ background: C.lightBg }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <Eyebrow>What We Look At</Eyebrow>
          <H2>The Bigger Picture, Together.</H2>
          <div className="mt-8 flex flex-wrap gap-3">
            {LOOK.map((l) => <span key={l} className="rounded-full px-4 py-2 text-sm bg-white" style={{ border: `1px solid ${C.line}`, color: C.primary }}>{l}</span>)}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Eyebrow>How It Works</Eyebrow>
        <H2>Three steps, entirely virtual.</H2>
        <div className="mt-8 grid md:grid-cols-3 gap-6">
          {STEPS.map((s) => (
            <div key={s.n} className="rounded-2xl p-6 bg-white" style={{ border: `1px solid ${C.line}` }}>
              <div className="text-3xl" style={{ ...SERIF, color: C.accent }}>{s.n}</div>
              <div className="mt-3 text-xl font-semibold text-slate-900" style={SERIF}>{s.t}</div>
              <p className="mt-2 text-sm text-slate-600">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Women's Wellness Analysis */}
      <section style={{ background: C.plum }} className="text-white" id="womens-wellness">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <Eyebrow dark>Women's Wellness Analysis™</Eyebrow>
          <h2 className="text-3xl sm:text-4xl" style={SERIF}>You know your body. <span style={{ fontStyle: "italic", color: C.lavender }}>And lately, something just feels different.</span></h2>
          <p className="mt-5" style={{ color: "#D9CCEA" }}>The Women's Wellness Analysis™ provides a personalized way to look at the bigger picture — including nutrition, sleep, stress, digestion, lifestyle, women's wellness concerns, hair, and other relevant wellness patterns — so you can better understand where your body may need support.</p>
          <div className="mt-8"><PrimaryBtn to={ROUTES.wellnessAssessment}>Start My Women's Wellness Analysis <ArrowRight className="h-4 w-4" /></PrimaryBtn></div>
        </div>
      </section>

      {/* In person */}
      <section style={{ background: C.lightBg }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 flex flex-col sm:flex-row sm:items-center justify-between gap-5">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest" style={{ color: C.accent }}>Prefer an In-Person Service?</div>
            <p className="mt-2 text-lg text-slate-800" style={SERIF}>Hair &amp; scalp analysis, wellness scans, and new client evaluations are also available in person.</p>
          </div>
          <GhostBtn to={ROUTES.inClinicServices || "/in-clinic-services"} className="whitespace-nowrap">Explore In-Clinic Services</GhostBtn>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Eyebrow>What to Expect</Eyebrow>
        <H2>What to expect.</H2>
        <p className="mt-5 text-slate-600">After choosing an assessment, you'll complete it through our secure client system — sharing your history, lifestyle, and wellness patterns. From there, KISSED will help identify patterns and determine the most appropriate next step, whether that's personalized recommendations, a referral, or continued support.</p>
        <p className="mt-4 text-sm text-slate-500">KISSED Functional Wellness does not diagnose, cure, or treat medical conditions. Wellness assessments identify observable patterns and offer personalized, non-medical recommendations, with referral to licensed medical providers when appropriate.</p>
        <div className="mt-8 rounded-2xl p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4" style={{ border: `1px solid ${C.line}` }}>
          <p className="text-slate-800" style={SERIF}>Still not sure which assessment is right for you?</p>
          <PrimaryBtn to={ROUTES.startHere}>Help Me Choose <ArrowRight className="h-4 w-4" /></PrimaryBtn>
        </div>
      </section>
    </PublicLayout>
  );
}
